import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useAdminArticles, useDeleteAdminArticle, useUpdateAdminArticle,
} from "../../lib/queries";

interface FormState {
  title: string;
  excerpt: string;
  author: string;
  imageUrl: string;
  category: string;
  hidden: boolean;
}

const EMPTY: FormState = {
  title: "", excerpt: "", author: "", imageUrl: "",
  category: "eat", hidden: false,
};

export function AdminArticleEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Reuse the admin list query cache so edit doesn't re-fetch on its
  // own — same pattern as AdminEventEdit.
  const { data: all = [], isPending: listLoading } = useAdminArticles();
  const article = id ? all.find((a) => a.id === id) : undefined;

  const [form, setForm] = useState<FormState>(EMPTY);
  const [hydrated, setHydrated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setHydrated(false); }, [id]);
  useEffect(() => {
    if (article && !hydrated) {
      setForm({
        title: article.title ?? "",
        excerpt: article.excerpt ?? "",
        author: article.author ?? "",
        imageUrl: article.imageUrl ?? "",
        category: article.category ?? "eat",
        hidden: !!article.hidden,
      });
      setHydrated(true);
    }
  }, [article, hydrated]);

  const update = useUpdateAdminArticle();
  const del = useDeleteAdminArticle();

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.title.trim()) { setError("Title is required."); return; }
    if (!id) return;
    try {
      await update.mutateAsync({
        id,
        title: form.title.trim(),
        excerpt: form.excerpt.trim() || null,
        author: form.author.trim() || null,
        imageUrl: form.imageUrl.trim() || null,
        category: form.category.trim() || "eat",
        hidden: form.hidden,
      });
      navigate("/admin/articles");
    } catch (err: any) {
      setError(String(err?.message ?? err).slice(0, 200));
    }
  };

  const onDelete = async () => {
    if (!id) return;
    if (!confirm(`Delete "${article?.title}"? This cannot be undone — the next scrape will not re-import a deleted row because the URL hash matches.`)) return;
    try {
      await del.mutateAsync(id);
      navigate("/admin/articles");
    } catch (err: any) {
      setError(String(err?.message ?? err).slice(0, 200));
    }
  };

  if (listLoading && !hydrated) {
    return <div className="ma-mono" style={{ fontSize: 11, color: "var(--ma-ink-soft)", textAlign: "center", padding: 32 }}>loading…</div>;
  }
  if (!article && hydrated) {
    return (
      <div style={{ padding: 24 }}>
        <div className="ma-display" style={{ fontSize: 22, marginBottom: 8 }}>Article not found</div>
        <button onClick={() => navigate("/admin/articles")} style={primaryBtn}>back to list</button>
      </div>
    );
  }

  return (
    <form onSubmit={onSave} style={{ padding: "20px 14px 32px" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14 }}>
        <div>
          <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ma-ink-soft)" }}>
            Edit · {article?.source}
          </div>
          <div className="ma-display" style={{ fontSize: 22 }}>article</div>
        </div>
        <button type="button" onClick={() => navigate("/admin/articles")} style={secondaryBtn}>cancel</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "0 14px" }}>
        <div style={{ gridColumn: "1 / -1" }}>
          <Field label="Title *">
            <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required style={inputStyle} />
          </Field>
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <Field label="Excerpt">
            <textarea value={form.excerpt} onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))}
              rows={3} style={{ ...inputStyle, fontFamily: "var(--ma-body)", resize: "vertical" }} />
          </Field>
        </div>
        <Field label="Author">
          <input value={form.author} onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))} style={inputStyle} />
        </Field>
        <Field label="Category">
          <input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} style={inputStyle} />
        </Field>
        <div style={{ gridColumn: "1 / -1" }}>
          <Field label="Image URL">
            <input value={form.imageUrl} onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))} placeholder="https://…" style={inputStyle} />
          </Field>
        </div>

        <div style={{ gridColumn: "1 / -1", marginTop: 6 }}>
          <label style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "12px 14px",
            background: form.hidden ? "var(--ma-paper)" : "var(--ma-cream)",
            border: form.hidden ? "var(--ma-border) solid var(--ma-brick)" : "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            cursor: "pointer",
          }}>
            <input
              type="checkbox"
              checked={form.hidden}
              onChange={(e) => setForm((f) => ({ ...f, hidden: e.target.checked }))}
              style={{ accentColor: "var(--ma-brick)", width: 18, height: 18 }}
            />
            <div>
              <div className="ma-mono" style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: form.hidden ? "var(--ma-brick)" : "var(--ma-ink)", fontWeight: 600 }}>
                {form.hidden ? "hidden from consumers" : "visible to consumers"}
              </div>
              <div style={{ fontFamily: "var(--ma-body)", fontSize: 12, color: "var(--ma-ink-soft)", marginTop: 2 }}>
                Hidden articles stay in the DB so the next scrape doesn't re-import them, but don't appear on /eat.
              </div>
            </div>
          </label>
        </div>

        <div style={{ gridColumn: "1 / -1", marginTop: 8 }}>
          <div className="ma-mono" style={{ fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ma-ink-soft)", marginBottom: 4 }}>
            Source URL (read-only)
          </div>
          <a href={article?.url} target="_blank" rel="noopener noreferrer" style={{
            display: "block", padding: "10px 12px",
            background: "var(--ma-cream)",
            border: "1px dashed var(--ma-line)",
            borderRadius: "var(--ma-card-radius)",
            fontFamily: "var(--ma-mono)", fontSize: 12,
            color: "var(--ma-ink)", textDecoration: "none",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {article?.url}
          </a>
        </div>
      </div>

      {error && (
        <div className="ma-mono" style={{
          fontSize: 11, color: "var(--ma-brick)", letterSpacing: "0.08em",
          padding: "10px 12px", marginTop: 10,
          background: "var(--ma-cream)", border: "var(--ma-border) solid var(--ma-brick)",
          borderRadius: "var(--ma-card-radius)",
        }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
        <button type="submit" disabled={update.isPending} style={{ ...primaryBtn, flex: 1, padding: "14px 0", fontSize: 12 }}>
          {update.isPending ? "saving…" : "save changes"}
        </button>
        <button type="button" onClick={onDelete} disabled={del.isPending} style={{
          ...secondaryBtn, padding: "14px 16px", borderColor: "var(--ma-brick)", color: "var(--ma-brick)",
        }}>
          {del.isPending ? "deleting…" : "delete"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "block", marginBottom: 12, flex: 1 }}>
      <div className="ma-mono" style={{
        fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase",
        color: "var(--ma-ink-soft)", marginBottom: 4,
      }}>
        {label}
      </div>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "10px 12px",
  background: "var(--ma-cream)",
  border: "var(--ma-border) solid var(--ma-ink)",
  borderRadius: "var(--ma-card-radius)",
  fontFamily: "var(--ma-mono)", fontSize: 16,
  color: "var(--ma-ink)", boxSizing: "border-box",
};
const primaryBtn: React.CSSProperties = {
  padding: "10px 14px",
  background: "var(--ma-ink)", color: "var(--ma-paper)",
  border: "var(--ma-border) solid var(--ma-ink)",
  borderRadius: "var(--ma-card-radius)",
  fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.12em",
  textTransform: "uppercase", fontWeight: 600, cursor: "pointer",
};
const secondaryBtn: React.CSSProperties = {
  ...primaryBtn, background: "transparent", color: "var(--ma-ink)",
};
